import get from 'lodash/get';
import set from 'lodash/set';
import unset from 'lodash/unset';

import {
  ObservableObjectChange,
  PROPERTY_DELETED,
  CustomEventTarget,
  reverseIterator,
  ARRAY_ITEM_REMOVED,
} from '@libs/util-ts';

import type { VideoDescriptor } from '../VideoDescriptor';

/**
 * @emits update - When the undo stack changes, either by pushing a new change or undoing/reding a change. This indicates that canUndo/canRedo may have changed.
 */
export class VideoDescriptorUndoStack extends CustomEventTarget<{
  update: void;
}> {
  // Threshold of time between changes where they will be grouped together
  static GROUP_TIME_THRESHOLD = 500;

  // This is a stack of changes that have been applied, ordered from oldest to newest
  private _pastChangeGroups: Array<{
    timestamp: number;
    changes: ObservableObjectChange[];
  }> = [];
  // This is a stack of changes that have been undone, ordered from newest (least recently undone) to oldest (most recently undone). I'm sure that cleared things up.
  private _futureChangeGroups: Array<{
    timestamp: number;
    changes: ObservableObjectChange[];
  }> = [];

  // Change groups are read-only to consumers
  get pastChangeGroups() {
    return this._pastChangeGroups;
  }
  get futureChangeGroups() {
    return this._futureChangeGroups;
  }

  // Flag to ignore change events while undoing/redoing so we don't
  // register those changes as new changes
  shouldIgnoreChangeEvents = false;

  videoDescriptor: VideoDescriptor;

  constructor(videoDescriptor: VideoDescriptor) {
    super();

    this.videoDescriptor = videoDescriptor;

    this.videoDescriptor.addEventListener('change', (event) => {
      const observableObjectChange = event.detail;
      this.pushChange(observableObjectChange);
    });
  }

  pushChange(change: ObservableObjectChange) {
    if (this.shouldIgnoreChangeEvents) {
      return;
    }

    const timestamp = performance.now();
    const previousChangeGroup = this._pastChangeGroups.at(-1);

    if (
      previousChangeGroup &&
      timestamp - previousChangeGroup.timestamp <= VideoDescriptorUndoStack.GROUP_TIME_THRESHOLD
    ) {
      // Otherwise, add the change to the group
      previousChangeGroup.timestamp = timestamp;
      previousChangeGroup.changes.push(change);
    } else {
      // Otherwise, create a new change group
      this._pastChangeGroups.push({
        timestamp,
        changes: [change],
      });
    }

    // When a new change is pushed, the future changes stack is cleared
    this._futureChangeGroups.length = 0;

    this.dispatchEvent('update');
  }

  undo(): boolean {
    const undoChangeGroup = this._pastChangeGroups.pop();
    if (undoChangeGroup) {
      this._futureChangeGroups.push(undoChangeGroup);

      this.shouldIgnoreChangeEvents = true;

      // Apply the undo changes in reverse order in case the order of the grouped changes mattered
      for (const change of reverseIterator(undoChangeGroup.changes)) {
        const [keyPath, prevValue] = change;
        this.applyChange(keyPath, prevValue);
      }
      this.shouldIgnoreChangeEvents = false;

      this.dispatchEvent('update');

      return true;
    }

    return false;
  }

  redo(): boolean {
    const redoChangeGroup = this._futureChangeGroups.pop();
    if (redoChangeGroup) {
      this._pastChangeGroups.push(redoChangeGroup);

      this.shouldIgnoreChangeEvents = true;
      for (const change of redoChangeGroup.changes) {
        const [keyPath, , newValue] = change;
        this.applyChange(keyPath, newValue);
      }
      this.shouldIgnoreChangeEvents = false;

      this.dispatchEvent('update');

      return true;
    }

    return false;
  }

  private applyChange(keyPath: string, newValue: unknown) {
    if (keyPath === '') {
      console.error(
        'Invalid operation: the root video descriptor object path cannot be directly modified',
      );
    } else {
      if (newValue === PROPERTY_DELETED) {
        // lodash unset deletes the property at a given key path
        unset(this.videoDescriptor.rawData, keyPath);
      } else if (newValue === ARRAY_ITEM_REMOVED) {
        // ARRAY_ITEM_REMOVED signifies that an item was removed from the end of an array.
        // We need to be careful about this because things like Array.prototype.push() will not explicitly update
        // the array length, only add a new item to the end of the array. That means that if we were to rewind that change,
        // we may accidentally set the last item of the array to undefined instead of adjusting the length as intended.
        const lastDotIndex = keyPath.lastIndexOf('.');
        const arrayKeyPath = keyPath.slice(0, lastDotIndex);
        const array = get(this.videoDescriptor.rawData, arrayKeyPath);
        const arrayIndex = Number(keyPath.slice(lastDotIndex + 1));

        if (Array.isArray(array) && !Number.isNaN(arrayIndex)) {
          // If the array's length is longer than the removed index,
          // we need to adjust the length of the array to shift that index
          // out of bounds
          if (arrayIndex < array.length) {
            array.length = arrayIndex;
          }
        }

        unset(this.videoDescriptor.rawData, keyPath);
      } else {
        // set the new value at the given key path
        set(this.videoDescriptor.rawData, keyPath, newValue);
      }
    }
  }

  get canUndo(): boolean {
    return this._pastChangeGroups.length > 0;
  }

  get canRedo(): boolean {
    return this._futureChangeGroups.length > 0;
  }
}
